import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface StaffProfilesProps {
  nhSlug: string;
}

interface StaffMember {
  id: string;
  name: string;
  role: string;
  bio: string | null;
  photo_url: string | null;
}

export function StaffProfiles({ nhSlug }: StaffProfilesProps) {
  const [staff, setStaff] = useState<StaffMember[]>([]);

  useEffect(() => {
    const fetchStaff = async () => {
      const { data } = await supabase
        .from("nursing_home_staff")
        .select("*")
        .eq("nursing_home_id", nhSlug)
        .eq("is_published", true)
        .order("display_order", { ascending: true })
        .limit(6);
      if (data) setStaff(data);
    };
    fetchStaff();
  }, [nhSlug]);

  if (staff.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-grandpal-beige/20">
        <CardTitle className="text-lg text-grandpal-green-dark flex items-center gap-2">
          <Users className="h-5 w-5" />
          Meet Our Team
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {staff.map((member) => (
            <div key={member.id} className="flex items-start gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={member.photo_url || undefined} alt={member.name} />
                <AvatarFallback className="text-sm">
                  {member.name.split(" ").map(n => n[0]).join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <h4 className="font-semibold text-sm">{member.name}</h4>
                <p className="text-xs text-muted-foreground">{member.role}</p>
                {member.bio && (
                  <p className="text-xs mt-1 line-clamp-2">{member.bio}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
